import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { DemandLinksPanel } from "./components/integrations/DemandLinksPanel";
import { SyncPanel } from "./components/integrations/SyncPanel";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { IntegrationsLayout } from "./components/layout/IntegrationsLayout";
import { SettingsLayout } from "./components/layout/SettingsLayout";
import { requireAdmin, requireAuth } from "./lib/route-guards";
import { RootLayout } from "./routes/__root";
import { AccountDetailPage } from "./routes/dashboard/accounts/$id";
import { AccountsPage } from "./routes/dashboard/accounts/index";
import { DashboardPage } from "./routes/dashboard/index";
import { LogsPage } from "./routes/dashboard/logs";
import { ReportingPage } from "./routes/dashboard/reporting";
import { ResourcesPage } from "./routes/dashboard/resources";
import { SchedulePage } from "./routes/dashboard/schedule";
import { SettingsConstraintsPage } from "./routes/dashboard/settings/constraints";
import { SettingsDisciplinesPage } from "./routes/dashboard/settings/disciplines";
import { SettingsIntensityPage } from "./routes/dashboard/settings/intensity";
import { SettingsPodCompositionPage } from "./routes/dashboard/settings/pod-composition";
import { SettingsSignalsPage } from "./routes/dashboard/settings/signals";
import { SettingsSystemPage } from "./routes/dashboard/settings/system";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { SignalsPage } from "./routes/dashboard/signals";
import { StaffingRequestDetailPage } from "./routes/dashboard/staffing-requests/$id";
import { StaffingRequestsPage } from "./routes/dashboard/staffing-requests/index";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
});

function DashboardLayoutRoute() {
  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

const dashboardLayout = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  beforeLoad: requireAuth,
  component: DashboardLayoutRoute,
});

const dashboardIndex = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    solutionId?: string;
    preset?: string;
    status?: string;
    fit?: string;
    pod?: string;
    intensity?: string;
    tier?: string;
    expectation?: string;
    constraints?: string;
    resource?: string;
  } => {
    const str = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : undefined);
    return {
      solutionId: str(search.solutionId),
      preset: str(search.preset),
      status: str(search.status),
      fit: str(search.fit),
      pod: str(search.pod),
      intensity: str(search.intensity),
      tier: str(search.tier),
      expectation: str(search.expectation),
      constraints: str(search.constraints),
      resource: str(search.resource),
    };
  },
  component: function DashboardIndexRoute() {
    const { solutionId } = dashboardIndex.useSearch();
    return <DashboardPage initialSolutionId={solutionId} />;
  },
});

const resourcesRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/resources",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    disc?: string;
    comp?: string;
    compMin?: string;
    con?: string;
    conMin?: string;
    load?: string;
    sol?: string;
    acct?: string;
  } => {
    const str = (v: unknown) => (typeof v === "string" && v.length > 0 ? v : undefined);
    return {
      q: str(search.q),
      disc: str(search.disc),
      comp: str(search.comp),
      compMin: str(search.compMin),
      con: str(search.con),
      conMin: str(search.conMin),
      load: str(search.load),
      sol: str(search.sol),
      acct: str(search.acct),
    };
  },
  component: ResourcesPage,
});

const scheduleRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/schedule",
  component: SchedulePage,
});

const reportingRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/reporting",
  component: ReportingPage,
});

const staffingRequestsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/staffing-requests",
  component: StaffingRequestsPage,
});

// viewSignals is CEO/RM only (§5 permission matrix) — the same role set
// requireAdmin already gates settingsLayout on, so it's reused here rather
// than inventing a parallel guard for a single leaf route.
const signalsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/signals",
  beforeLoad: requireAdmin,
  component: SignalsPage,
});

// viewSystemLogs is CEO/RM only (§5 permission matrix), mirroring signalsRoute's
// reuse of requireAdmin rather than a parallel single-route guard.
const logsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/logs",
  beforeLoad: requireAdmin,
  component: LogsPage,
});

const staffingRequestDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/staffing-requests/$id",
  component: function StaffingRequestDetailRoute() {
    const { id } = staffingRequestDetailRoute.useParams();
    return <StaffingRequestDetailPage id={id} />;
  },
});

const accountsRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/accounts",
  component: AccountsPage,
});

const accountDetailRoute = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/accounts/$id",
  component: function AccountDetailRoute() {
    const { id } = accountDetailRoute.useParams();
    return <AccountDetailPage id={id} />;
  },
});

function SettingsLayoutRoute() {
  return (
    <SettingsLayout>
      <Outlet />
    </SettingsLayout>
  );
}

const settingsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/settings",
  beforeLoad: async (ctx) => {
    await requireAdmin();
    if (ctx.location.pathname === "/dashboard/settings") {
      throw redirect({ to: "/dashboard/settings/users" });
    }
  },
  component: SettingsLayoutRoute,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/users",
  component: SettingsUsersPage,
});

const settingsDisciplinesRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/disciplines",
  component: SettingsDisciplinesPage,
});

const settingsPodCompositionRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/pod-composition",
  component: SettingsPodCompositionPage,
});

const settingsSystemRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/system",
  component: SettingsSystemPage,
});

const settingsConstraintsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/constraints",
  component: SettingsConstraintsPage,
});

const settingsIntensityRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/intensity",
  component: SettingsIntensityPage,
});

// Integrations moved out of Settings to its own left-sidebar page. Keep this
// route as a redirect so old /dashboard/settings/integrations links keep working.
const settingsIntegrationsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/integrations",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard/integrations/sync" });
  },
});

const settingsSignalsRoute = createRoute({
  getParentRoute: () => settingsLayout,
  path: "/signals",
  component: SettingsSignalsPage,
});

// ── Integrations (top-level, admin-gated) ─────────────────────────────────────
// A dedicated admin layout route (guard on the layout, not on leaves — per the
// frontend routing rule) so Integrations lives in the left sidebar with its own
// tab strip rather than inside the Settings panel.
function IntegrationsLayoutRoute() {
  return (
    <IntegrationsLayout>
      <Outlet />
    </IntegrationsLayout>
  );
}

const integrationsLayout = createRoute({
  getParentRoute: () => dashboardLayout,
  path: "/integrations",
  beforeLoad: async (ctx) => {
    await requireAdmin();
    if (ctx.location.pathname === "/dashboard/integrations") {
      throw redirect({ to: "/dashboard/integrations/sync" });
    }
  },
  component: IntegrationsLayoutRoute,
});

const integrationsSyncRoute = createRoute({
  getParentRoute: () => integrationsLayout,
  path: "/sync",
  component: SyncPanel,
});

const integrationsDemandsRoute = createRoute({
  getParentRoute: () => integrationsLayout,
  path: "/demands",
  component: DemandLinksPanel,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardLayout.addChildren([
    dashboardIndex,
    resourcesRoute,
    scheduleRoute,
    reportingRoute,
    staffingRequestsRoute,
    staffingRequestDetailRoute,
    signalsRoute,
    logsRoute,
    accountsRoute,
    accountDetailRoute,
    settingsLayout.addChildren([
      settingsUsersRoute,
      settingsDisciplinesRoute,
      settingsPodCompositionRoute,
      settingsConstraintsRoute,
      settingsIntensityRoute,
      settingsSystemRoute,
      settingsIntegrationsRoute,
      settingsSignalsRoute,
    ]),
    integrationsLayout.addChildren([integrationsSyncRoute, integrationsDemandsRoute]),
  ]),
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
